import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import './ShareToRider.css';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mr', name: 'Marathi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'pa', name: 'Punjabi' },
];

const PLATFORMS = [
  { id: 'swiggy', name: 'Swiggy', icon: '🍔', color: '#fc8019' },
  { id: 'zomato', name: 'Zomato', icon: '🍕', color: '#e23744' },
  { id: 'blinkit', name: 'Blinkit', icon: '⚡', color: '#f8cb46' },
  { id: 'bigbasket', name: 'BigBasket', icon: '🛒', color: '#84c225' },
  { id: 'amazon', name: 'Amazon', icon: '📦', color: '#ff9900' },
  { id: 'flipkart', name: 'Flipkart', icon: '🛍️', color: '#2874f0' },
  { id: 'other', name: 'Other', icon: '📱', color: '#666' },
];

const ShareToRider = ({ onClose, onShareSuccess }) => {
  const [mode, setMode] = useState('auto'); // 'auto' or 'manual'
  const [riderPhone, setRiderPhone] = useState('');
  const [riderName, setRiderName] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('hi');
  const [orderId, setOrderId] = useState('');
  const [platform, setPlatform] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sentDirectly, setSentDirectly] = useState(false);

  // Auto-detection states
  const [detectedRider, setDetectedRider] = useState(null);
  const [loadingDetection, setLoadingDetection] = useState(false);
  const [accessibilityEnabled, setAccessibilityEnabled] = useState(false);
  const [riderHistory, setRiderHistory] = useState([]);

  // Fetch detected rider and settings on mount
  useEffect(() => {
    fetchLatestDetectedRider();
    fetchAccessibilitySettings();
    fetchRiderHistory();
  }, []);

  const fetchLatestDetectedRider = async () => {
    setLoadingDetection(true);
    try {
      const response = await api.get('/accessibility/rider/latest');

      if (response.data.success) {
        if (response.data.data.pendingRider) {
          setDetectedRider(response.data.data.pendingRider);
          // Auto-fill from detected rider
          setRiderPhone(response.data.data.pendingRider.riderPhone);
          setRiderName(response.data.data.pendingRider.riderName || '');
          setPlatform(response.data.data.pendingRider.platform || '');
          setOrderId(response.data.data.pendingRider.orderId || '');
        }
      }
    } catch (error) {
      console.error('Error fetching detected rider:', error);
    } finally {
      setLoadingDetection(false);
    }
  };

  const fetchAccessibilitySettings = async () => {
    try {
      const response = await api.get('/accessibility/settings');

      if (response.data.success) {
        setAccessibilityEnabled(response.data.data.enabled);
        setTargetLanguage(response.data.data.preferredRiderLanguage || 'hi');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchRiderHistory = async () => {
    try {
      const response = await api.get('/accessibility/rider/history');

      if (response.data.success) {
        setRiderHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    return /^(\+91)?[6-9]\d{9}$/.test(cleanPhone);
  };

  const generateShareLink = async () => {
    if (!validatePhone(riderPhone)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsGenerating(true);
    try {
      // Use auto-send endpoint if from auto mode
      const endpoint = mode === 'auto' && detectedRider
        ? '/accessibility/auto-send'
        : '/share/generate';

      const response = await api.post(endpoint, {
        riderPhone,
        riderName: riderName || 'Delivery Partner',
        targetLanguage,
        orderId: orderId || null,
        platform: platform || null
      });

      if (!response.data.success) {
        toast.error(response.data.message || 'Failed to generate link');
        return;
      }

      setShareData(response.data.data);
      toast.success('Share link generated!');

      if (onShareSuccess) {
        onShareSuccess(response.data.data);
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setIsGenerating(false);
    }
  };

  // Send directly via app (privacy-preserving - customer phone not exposed)
  const sendDirectlyToRider = async () => {
    if (!validatePhone(riderPhone)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSending(true);
    try {
      const response = await api.post('/share/send-to-rider', {
        riderPhone,
        riderName: riderName || 'Delivery Partner',
        orderId: orderId || null
      });

      if (response.data.success) {
        setShareData(response.data.data);
        setSentDirectly(true);
        toast.success('Instructions sent to rider!');

        if (onShareSuccess) {
          onShareSuccess(response.data.data);
        }
      } else {
        toast.error(response.data.message || 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending to rider:', error);
      toast.error('Failed to send instructions to rider');
    } finally {
      setIsSending(false);
    }
  };

  const openWhatsApp = () => {
    if (shareData?.whatsappLink) {
      window.open(shareData.whatsappLink, '_blank');
    }
  };

  const copyLink = async () => {
    if (shareData?.shareLink) {
      try {
        await navigator.clipboard.writeText(shareData.shareLink);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
    }
  };

  const shareViaOther = async () => {
    if (shareData?.shareLink && navigator.share) {
      try {
        await navigator.share({
          title: 'Delivery Instructions',
          text: 'Here are my delivery instructions to help you find my address',
          url: shareData.shareLink
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  const selectFromHistory = (rider) => {
    setRiderPhone(rider.riderPhone);
    setRiderName(rider.riderName || '');
    setPlatform(rider.platform || '');
    setOrderId(rider.orderId || '');
    setMode('manual');
  };

  const resetForm = () => {
    setShareData(null);
    setRiderPhone('');
    setRiderName('');
    setOrderId('');
    setPlatform('');
    setDetectedRider(null);
    setSentDirectly(false);
    fetchLatestDetectedRider();
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={e => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>📤 Share with Delivery Partner</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="share-modal-body">
          {!shareData ? (
            <>
              {/* Mode Selection */}
              <div className="mode-selector">
                <button
                  className={`mode-btn ${mode === 'auto' ? 'active' : ''}`}
                  onClick={() => setMode('auto')}
                >
                  <span className="mode-icon">🤖</span>
                  <span className="mode-label">Automatic</span>
                  <span className="mode-desc">Auto-detected rider</span>
                </button>
                <button
                  className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
                  onClick={() => setMode('manual')}
                >
                  <span className="mode-icon">✏️</span>
                  <span className="mode-label">Manual</span>
                  <span className="mode-desc">Enter rider details</span>
                </button>
              </div>

              {mode === 'auto' ? (
                // Automatic Mode
                <div className="auto-mode-content">
                  {loadingDetection ? (
                    <div className="loading-detection">
                      <div className="mini-spinner"></div>
                      <p>Checking for detected riders...</p>
                    </div>
                  ) : detectedRider ? (
                    <div className="detected-rider-card">
                      <div className="detected-badge">
                        <span className="pulse"></span>
                        Auto-Detected
                      </div>
                      <div className="rider-info-display">
                        <div className="platform-badge" style={{
                          background: PLATFORMS.find(p => p.id === detectedRider.platform)?.color || '#666'
                        }}>
                          {PLATFORMS.find(p => p.id === detectedRider.platform)?.icon || '📱'}
                          {PLATFORMS.find(p => p.id === detectedRider.platform)?.name || detectedRider.platform}
                        </div>
                        <div className="rider-details">
                          <p className="rider-name">
                            👤 {detectedRider.riderName || 'Delivery Partner'}
                          </p>
                          <p className="rider-phone">
                            📱 +91 {detectedRider.riderPhone}
                          </p>
                          {detectedRider.orderId && (
                            <p className="order-id">📦 Order: {detectedRider.orderId}</p>
                          )}
                          <p className="detected-time">
                            🕐 Detected {new Date(detectedRider.detectedAt).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <span className="label-icon">🌐</span>
                          Rider's Language
                        </label>
                        <select
                          value={targetLanguage}
                          onChange={e => setTargetLanguage(e.target.value)}
                        >
                          {LANGUAGES.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                          ))}
                        </select>
                      </div>

                      <button
                        className="btn-generate auto"
                        onClick={sendDirectlyToRider}
                        disabled={isSending}
                      >
                        {isSending ? (
                          <>
                            <span className="mini-spinner"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <span>📤</span>
                            Send to Rider
                          </>
                        )}
                      </button>
                      <p className="privacy-note">Your phone number will not be shared with the rider</p>
                    </div>
                  ) : (
                    <div className="no-detection">
                      <div className="no-detection-icon">🔍</div>
                      <h3>No Rider Detected</h3>
                      <p>
                        {accessibilityEnabled
                          ? 'Waiting for delivery app notification with rider details...'
                          : 'Enable accessibility service to auto-detect rider numbers from delivery apps.'}
                      </p>
                      {!accessibilityEnabled && (
                        <button
                          className="btn-enable-accessibility"
                          onClick={() => window.location.href = '/accessibility-settings'}
                        >
                          Enable Accessibility
                        </button>
                      )}
                      <button
                        className="btn-switch-manual"
                        onClick={() => setMode('manual')}
                      >
                        Enter Manually Instead
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // Manual Mode
                <div className="manual-mode-content">
                  <p className="share-description">
                    Enter the rider's details to send them your delivery instructions via WhatsApp.
                  </p>

                  {/* Platform Selection */}
                  <div className="form-group">
                    <label>
                      <span className="label-icon">🛵</span>
                      Delivery Platform (Optional)
                    </label>
                    <div className="platform-grid">
                      {PLATFORMS.map(p => (
                        <button
                          key={p.id}
                          className={`platform-chip ${platform === p.id ? 'selected' : ''}`}
                          onClick={() => setPlatform(platform === p.id ? '' : p.id)}
                          style={platform === p.id ? { background: p.color, borderColor: p.color, color: '#fff' } : {}}
                        >
                          <span>{p.icon}</span>
                          <span>{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="label-icon">📱</span>
                      Rider's Mobile Number *
                    </label>
                    <div className="phone-input-group">
                      <span className="country-code">+91</span>
                      <input
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        value={riderPhone}
                        onChange={e => setRiderPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        maxLength="10"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="label-icon">👤</span>
                      Rider's Name (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Rahul"
                      value={riderName}
                      onChange={e => setRiderName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="label-icon">🌐</span>
                      Rider's Preferred Language
                    </label>
                    <select
                      value={targetLanguage}
                      onChange={e => setTargetLanguage(e.target.value)}
                    >
                      {LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                    <small>Instructions will be translated to this language</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="label-icon">📦</span>
                      Order ID (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., ORD123456"
                      value={orderId}
                      onChange={e => setOrderId(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn-generate primary"
                    onClick={sendDirectlyToRider}
                    disabled={isSending || !riderPhone}
                  >
                    {isSending ? (
                      <>
                        <span className="mini-spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span>📤</span>
                        Send to Rider via SMS
                      </>
                    )}
                  </button>
                  <p className="privacy-note">Your phone number will not be shared with the rider</p>

                  <button
                    className="btn-generate secondary"
                    onClick={generateShareLink}
                    disabled={isGenerating || !riderPhone}
                  >
                    {isGenerating ? (
                      <>
                        <span className="mini-spinner"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span>🔗</span>
                        Get Link to Share Manually
                      </>
                    )}
                  </button>

                  {/* Recent Riders Quick Select */}
                  {riderHistory.length > 0 && (
                    <div className="recent-riders">
                      <h4>Recent Riders</h4>
                      <div className="riders-list">
                        {riderHistory.slice(0, 3).map((rider, idx) => (
                          <button
                            key={idx}
                            className="rider-quick-select"
                            onClick={() => selectFromHistory(rider)}
                          >
                            <span className="platform-mini">
                              {PLATFORMS.find(p => p.id === rider.platform)?.icon || '📱'}
                            </span>
                            <span className="rider-phone-mini">+91 {rider.riderPhone}</span>
                            {rider.instructionsSent && <span className="sent-badge">Sent</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            // Success State
            <div className="share-success">
              <div className="success-icon">{sentDirectly ? '📨' : '✅'}</div>
              <h3>{sentDirectly ? 'Instructions Sent to Rider!' : 'Link Generated Successfully!'}</h3>

              {sentDirectly && (
                <div className="sent-confirmation">
                  <p>The rider will receive an SMS with your delivery instructions.</p>
                  <p className="privacy-badge">🔒 Your phone number was not shared</p>
                </div>
              )}

              <div className="share-link-box">
                <input
                  type="text"
                  value={shareData.shareLink}
                  readOnly
                />
                <button onClick={copyLink}>
                  {copied ? '✓' : '📋'}
                </button>
              </div>

              {!sentDirectly && (
                <div className="share-actions">
                  <button className="btn-whatsapp" onClick={openWhatsApp}>
                    <span>💬</span>
                    Open WhatsApp
                  </button>

                  <button className="btn-share-other" onClick={shareViaOther}>
                    <span>📤</span>
                    Share via Other
                  </button>
                </div>
              )}

              <div className="share-info">
                {platform && (
                  <p>
                    <strong>Platform:</strong> {PLATFORMS.find(p => p.id === platform)?.name || platform}
                  </p>
                )}
                <p>
                  <strong>Rider:</strong> {riderName || 'Delivery Partner'}
                </p>
                <p>
                  <strong>Phone:</strong> +91 {riderPhone}
                </p>
                <p>
                  <strong>Language:</strong> {LANGUAGES.find(l => l.code === targetLanguage)?.name}
                </p>
                <p className="expiry-note">
                  ⏰ Link expires in 24 hours
                </p>
              </div>

              <button
                className="btn-new-link"
                onClick={resetForm}
              >
                Share with Another Rider
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareToRider;
